import React, { useState } from 'react';
import {
  OrderHistory,
  useOrderHistory,
} from '../../../fetch/fetchOrderHistory';
import './OrderHistoryView.scss';
import dayjs from 'dayjs';
import { capitalize } from '../../../utils/strings';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';

type OrderHistoryViewProps = {
  orderId: string;
};

const OrderHistoryView = (props: OrderHistoryViewProps): JSX.Element => {
  const orderHistoryResult = useOrderHistory(props.orderId);
  const [showMore, setShowMore] = useState(false);
  const faAngleStyle = {
    width: 30,
    height: 30,
    color: '#3a3d4b',
    cursor: 'pointer',
  };

  function onShowMore() {
    setShowMore(!showMore);
  }

  if (orderHistoryResult.success === false) {
    return <p> {orderHistoryResult.errorMessage} </p>;
  }

  // TEMP SOLUTION
  // Dispatchers find the current presentation of the delivery window changes confusing and wanted
  // to merge the two events into one. As this requires a bigger change and data migration on the
  // backend it was suggested to fix this on the client side for now.
  const orderHistory: OrderHistory[] = orderHistoryResult.orderHistory.reduce(
    (result: OrderHistory[], currentOrderHistory: OrderHistory) => {
      const operationsToMerge = ['DELIVERY_TO', 'DELIVERY_FROM'];
      if (
        operationsToMerge.includes(currentOrderHistory.operation) &&
        !result.find(
          orderHistory =>
            orderHistory.createdAt === currentOrderHistory.createdAt
        )
      ) {
        const nextOrderHistory: OrderHistory =
          orderHistoryResult.orderHistory.find(
            orderHistory =>
              orderHistory.createdAt === currentOrderHistory.createdAt &&
              orderHistory.operation !== currentOrderHistory.operation
          );
        result.push({
          ...currentOrderHistory,
          operation: 'DELIVERY',
          oldValue: `${dayjs(nextOrderHistory.oldValue).format(
            'DD.MM - HH:mm'
          )} - ${dayjs(currentOrderHistory.oldValue).format('HH:mm')}`,
          newValue: `${dayjs(nextOrderHistory.newValue).format(
            'DD.MM - HH:mm'
          )} - ${dayjs(currentOrderHistory.newValue).format('HH:mm')}`,
        });
      }

      // There are currently no other operations, but might be in the future
      if (!operationsToMerge.includes(currentOrderHistory.operation)) {
        result.push(currentOrderHistory);
      }

      return result;
    },
    []
  );

  return (
    <div className="orderHistoryView" onClick={() => onShowMore()}>
      {orderHistory.length > 0 ? (
        <>
          <h5>See all changes ({orderHistory.length})</h5>
          {!showMore && (
            <FaAngleDown
              style={faAngleStyle}
              className="desktop-only"
              onClick={() => onShowMore()}
            />
          )}

          {showMore && (
            <>
              <FaAngleUp
                style={faAngleStyle}
                className="desktop-only"
                onClick={() => onShowMore()}
              />
              <table>
                <thead>
                  <tr>
                    <th>Change</th>
                    <th>From</th>
                    <th>To</th>
                    <th>User</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {orderHistory.map((history, i) => (
                    <OrderHistoryGroupRows
                      key={i}
                      orderHistory={history}
                      evenRow={i % 2 === 0}
                    />
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      ) : (
        <h5 className={'noHistoryToDisplay'}>This order has no changes.</h5>
      )}
    </div>
  );
};

const OrderHistoryGroupRows = ({
  orderHistory,
  evenRow,
}: {
  orderHistory: OrderHistory;
  evenRow: boolean;
}) => {
  return (
    <>
      <tr className={evenRow ? 'evenHistoryRow' : 'oddHistoryRow'}>
        <td>{capitalize(orderHistory.operation.replace('_', ' '))}</td>
        <td>{orderHistory.oldValue}</td>
        <td>{orderHistory.newValue}</td>
        <td>{orderHistory.userName === '' ? '-' : orderHistory.userName}</td>
        <td>{dayjs(orderHistory.createdAt).format('DD.MM.YY - HH:mm')}</td>
      </tr>
    </>
  );
};

export default OrderHistoryView;
